import * as Sentry from "@sentry/react";
import { isChunkReloadInFlight } from "@/lib/chunkReloadState";

// A stale webview session holds chunk filenames from a bundle that no longer
// exists. The dynamic import then resolves to index.html (or 404s), and React
// throws from inside the lazy component. Sentry JAVASCRIPT-REACT-11 and -12 are
// both this shape, and both were handled:no — nothing caught them, so the screen
// stayed blank and a tap appeared to do nothing at all.
//
// These five patterns are the observed messages across Safari, Chrome and the
// Median WKWebView. Two of them are Safari/WKWebView wording and they are not
// interchangeable: the MIME-type one is what WebKit says when a chunk request
// comes back as index.html, and "Importing a module script failed" is what it
// says when the dynamic import fails outright (404, dropped connection, a
// chunk that is simply gone). Chrome spells the second case "Failed to fetch
// dynamically imported module" and Firefox "error loading dynamically imported
// module" — both were here from the start, so iOS was the one platform whose
// outright-failure message nothing matched. The last one is React.lazy's own
// failure shape: it rethrows the rejected module record and the message
// mentions `_result.default`.
//
// ⚠️ Sentry JAVASCRIPT-REACT-35 (release 7f4adbf) is the WebKit outright-failure
// message, and it reached the GENERIC fallback — "Something went wrong. Please
// refresh the page." — because that string matched none of the four patterns
// this list had. A refresh button is the wrong advice for a stale bundle (see
// ChunkSkewFallbackUI), and the event carried no `reload_in_flight` tag, so the
// population it belongs to was invisible to triage.
const CHUNK_SKEW_PATTERNS = [
  /is not a valid JavaScript MIME type/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /_result\.default/,
];

const messageOf = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
};

export const isChunkSkewError = (error: unknown): boolean => {
  const message = messageOf(error);
  if (!message) return false;
  return CHUNK_SKEW_PATTERNS.some((pattern) => pattern.test(message));
};

const shellStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100dvh",
  padding: "2rem",
  background: "#1C1A3E",
  color: "#fff",
  textAlign: "center",
  gap: "1rem",
};

const FallbackUI = () => (
  <div style={shellStyle}>
    <p style={{ fontSize: "1.1rem", opacity: 0.9 }}>
      Something went wrong. Please refresh the page.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: "0.75rem 1.5rem",
        borderRadius: "9999px",
        background: "#7C5CFC",
        color: "#fff",
        border: "none",
        cursor: "pointer",
        fontSize: "1rem",
      }}
    >
      Refresh
    </button>
  </div>
);

// Deliberately no button and no reload. An in-page reload does not clear a
// stale webview bundle reliably, and offering a control that silently does
// nothing is the failure we are fixing, not a fix for it.
//
// ⚠️ This is the STUCK screen — only correct when nothing is already fixing it.
// See chunkSkewFallbackKind below for when it is the wrong thing to show.
const ChunkSkewFallbackUI = () => (
  <div style={shellStyle}>
    <p style={{ fontSize: "1.1rem", opacity: 0.9 }}>
      Something didn&rsquo;t load. Close Kin completely and reopen it.
    </p>
  </div>
);

// #1251 — shown for the few hundred ms between main.tsx scheduling a reload and
// the browser committing it. She is about to get a working app without doing
// anything, so telling her to close and reopen would be both alarming and
// wrong. No button: the reload is already on its way and a second one would
// race it.
const ChunkReloadingUI = () => (
  <div style={shellStyle}>
    <p style={{ fontSize: "1.1rem", opacity: 0.9 }}>Updating Kin&hellip;</p>
  </div>
);

export type FallbackKind = "reloading" | "chunk_stuck" | "generic";

/**
 * Which screen an error should produce.
 *
 * Split out from the render so the decision is unit-testable — the three-way
 * choice is the whole of #1251 and it should not only be reachable by mounting
 * an error boundary.
 */
export const chunkSkewFallbackKind = (error: unknown, now?: number): FallbackKind => {
  if (!isChunkSkewError(error)) return "generic";
  return isChunkReloadInFlight(now) ? "reloading" : "chunk_stuck";
};

export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ({ error }) => {
      switch (chunkSkewFallbackKind(error)) {
        case "reloading":
          return <ChunkReloadingUI />;
        case "chunk_stuck":
          return <ChunkSkewFallbackUI />;
        default:
          return <FallbackUI />;
      }
    },
    // Tags the event the boundary already sends, rather than raising a second
    // one via captureException, so the original stack and breadcrumbs stay on
    // the same issue.
    //
    // #1251 adds `reload_in_flight`, which splits JAVASCRIPT-REACT-11/-12 into
    // the two populations that were previously indistinguishable:
    //   true  — self-healing; a reload was already scheduled and she saw
    //           "Updating Kin…" for a moment. Expected, not a defect.
    //   false — genuinely stuck; no reload was coming (the 60s guard suppressed
    //           it, or the error arrived by another route) and she saw the
    //           close-and-reopen screen. This is the population worth chasing.
    //
    // Deliberately tagged rather than dropped: these events are the only
    // evidence for whether the TypeError beats the navigation, which is the one
    // part of #1251's mechanism not provable from source.
    beforeCapture: (scope, error) => {
      if (isChunkSkewError(error)) {
        scope.setTag("error_class", "chunk_skew");
        scope.setTag("reload_in_flight", String(isChunkReloadInFlight()));
      }
    },
  }
);
